import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from 'firebase/auth';
import { auth } from '../firebase';
import axios from 'axios';
import { API_BASE_URL, getAuthHeaders } from '../api';

interface UserFeatures {
  competitionSetup?: boolean;
  admin?: boolean;
  clubOwner?: boolean;
}

interface User {
  uid: string;
  email: string | null;
  displayName: string | null;
  features?: UserFeatures;
  competitionSetup?: boolean; // Firebase custom claim — true for competition organisers
  admin?: boolean;            // Firebase custom claim — true for super admins
  clubOwner?: boolean;        // Firebase custom claim — true for club owners
  clubId?: number | null;     // from /api/me — app_user.club_id or owned club row
}

/** Outcome of an email/password sign-in attempt. */
export type LoginResult =
  | { success: true }
  | { success: false; invalidCredentials: boolean };

interface AuthContextType {
  user: User | null;
  features: UserFeatures;
  login: (email: string, password: string) => Promise<LoginResult>;
  signup: (email: string, password: string) => Promise<LoginResult>;
  logout: () => void;
  changePassword: (currentPassword: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
  loading: boolean;
  refreshMe: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

function firebaseAuthErrorCode(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null || !('code' in error)) return undefined;
  const code = (error as { code: unknown }).code;
  return typeof code === 'string' ? code : undefined;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [features, setFeatures] = useState<UserFeatures>({});
  const [loading, setLoading] = useState(true);

  const fetchMe = React.useCallback(async () => {
    const headers = await getAuthHeaders();
    if (!headers?.Authorization) return;
    try {
      const res = await axios.get<{
        uid: string;
        email?: string;
        admin?: boolean;
        competitionSetup?: boolean;
        clubOwner?: boolean;
        clubId?: number | null;
        features?: UserFeatures;
      }>(`${API_BASE_URL}/api/me`, { headers });
      const data = res.data;
      const feats: UserFeatures = {
        ...(data?.features || {}),
        admin: data?.admin === true,
        competitionSetup: data?.competitionSetup === true,
        clubOwner: data?.clubOwner === true,
      };
      setFeatures(feats);

      let tokenClaims: Record<string, unknown> = {};
      if (auth.currentUser) {
        const tokenResult = await auth.currentUser.getIdTokenResult(true);
        tokenClaims = tokenResult.claims;
        axios.defaults.headers.common['Authorization'] = `Bearer ${tokenResult.token}`;
      }

      const clubId =
        typeof data?.clubId === 'number' && Number.isFinite(data.clubId) ? data.clubId : null;

      setUser((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          admin: feats.admin === true || tokenClaims['admin'] === true,
          competitionSetup: feats.competitionSetup === true || tokenClaims['competitionSetup'] === true,
          clubOwner: feats.clubOwner === true || tokenClaims['clubOwner'] === true,
          clubId,
          features: feats,
        };
      });
    } catch (error) {
      console.warn('GET /api/me failed; role flags may be stale until next login.', error);
      setFeatures({});
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Force-refresh to pick up any custom claims set server-side since last login
        try {
          const tokenResult = await firebaseUser.getIdTokenResult(true);
          axios.defaults.headers.common['Authorization'] = `Bearer ${tokenResult.token}`;
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            competitionSetup: tokenResult.claims['competitionSetup'] === true,
            admin: tokenResult.claims['admin'] === true,
            clubOwner: tokenResult.claims['clubOwner'] === true,
          });
        } catch (error) {
          console.error('Error getting Firebase token:', error);
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
          });
        }
        await fetchMe();
      } else {
        setUser(null);
        setFeatures({});
        delete axios.defaults.headers.common['Authorization'];
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [fetchMe]);

  const refreshMe = React.useCallback(async () => {
    if (auth.currentUser) await fetchMe();
  }, [fetchMe]);

  /**
   * Signs in with Firebase email/password. Distinguishes invalid credentials from other failures.
   */
  const login = async (email: string, password: string): Promise<LoginResult> => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      return { success: true };
    } catch (error: unknown) {
      console.error('Login failed:', error);
      const code = firebaseAuthErrorCode(error);
      const invalidCredentials =
        code === 'auth/wrong-password' ||
        code === 'auth/user-not-found' ||
        code === 'auth/invalid-credential' ||
        code === 'auth/invalid-email' ||
        code === 'auth/invalid-login-credentials';
      return { success: false, invalidCredentials };
    }
  };

  /**
   * Creates a Firebase email/password account for athlete self-signup.
   */
  const signup = async (email: string, password: string): Promise<LoginResult> => {
    try {
      await createUserWithEmailAndPassword(auth, email, password);
      return { success: true };
    } catch (error: unknown) {
      console.error('Signup failed:', error);
      const code = firebaseAuthErrorCode(error);
      const invalidCredentials =
        code === 'auth/invalid-email' ||
        code === 'auth/weak-password' ||
        code === 'auth/email-already-in-use';
      return { success: false, invalidCredentials };
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser || !currentUser.email) {
        return { success: false, error: 'No user logged in' };
      }

      // Re-authenticate user with current password
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
      await reauthenticateWithCredential(currentUser, credential);

      // Update password
      await updatePassword(currentUser, newPassword);

      return { success: true };
    } catch (error: any) {
      console.error('Password change failed:', error);
      let errorMessage = 'Failed to change password';
      
      if (error.code === 'auth/wrong-password') {
        errorMessage = 'Current password is incorrect';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'New password is too weak';
      } else if (error.code === 'auth/requires-recent-login') {
        errorMessage = 'Please log in again before changing password';
      }
      
      return { success: false, error: errorMessage };
    }
  };

  const value: AuthContextType = {
    user,
    features,
    login,
    signup,
    logout,
    changePassword,
    loading,
    refreshMe,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
